import { createSelector } from 'reselect';
import {
  makeNullSafeDomainGetter,
  agentByIdSelector,
  agentBulletinsByIdSelector,
  agentContactInfoByIdSelector,
  createIsLoadingSelector,
  publicRealEstateAgentByIdSelector,
} from 'store/state/domainData/selectors/common';
import { IAgentOfficeDeal, IAgentOfficeDealSeller, DealAffiliationType } from 'utils/entities';
import uuid from 'uuid/v4';
import { UiAgentOfficeDeal } from 'screens/AgentOfficePages/common/sections/deals/types';


export const agentPageDataSelector = makeNullSafeDomainGetter(agentByIdSelector, 'getRealEstateAgentById');
export const agentContactInfoDataSelector = makeNullSafeDomainGetter(agentContactInfoByIdSelector, 'getRealEstateAgentById');
export const publicAgentPageDataSelector = makeNullSafeDomainGetter(publicRealEstateAgentByIdSelector, 'getPublicRealEstateAgentById');
export const isPublicAgentLoadingSelector = createIsLoadingSelector(publicRealEstateAgentByIdSelector);
export const safeAgentBulletinsByIdSelector = makeNullSafeDomainGetter(agentBulletinsByIdSelector, 'getRealEstateAgentById');
export const safeAgentDataForSoldDealBannerSelector = makeNullSafeDomainGetter(publicRealEstateAgentByIdSelector, 'getPublicRealEstateAgentById');

export const agentBulletinsDataSelector = createSelector([ safeAgentBulletinsByIdSelector, agentPageDataSelector ], (listings, agentData) => {
  if (!listings) return null;

  const dealsByAgent = listings.deals && listings.deals.length ? (
    listings.deals.reduce((acc, d: IAgentOfficeDeal) => {
      if (!d.location) return acc;

      const agentList = d.agents ? d.agents.filter(a => a.agentId === agentData.agentId) : [];
      if (!agentList.length) return acc;

      const dealsWithSameAgent = agentList.map(a => ({
        ...d,
        agents: [ a ],
      }));
      return [ ...acc, ...dealsWithSameAgent ];
    }, [])
  ) : [];
  return {
    ...listings,
    deals: dealsByAgent,
  };
});

export const agentPageBulletinSelector = createSelector([ agentBulletinsDataSelector, agentPageDataSelector ], (agent, agentPage) => {

  if (!agent) return [];
  const dealsEnriched = agent.deals ? agent.deals.reduce((acc, d: IAgentOfficeDeal) => {
    if (!d.location) return acc;

    const agentList = d.agents ? d.agents.filter(a => a.agentId === agentPage.agentId) : [];
    if (!agentList.length) return acc;

    const dealsWithSameAgent = agentList.map(a => ({
      ...d,
      agents: [ a ],
      type: 'sold',
      locationPoint: { lng: d.location.coordinates[0], lat: d.location.coordinates[1] },
      dealType: 'sold',
      id: d.saleId,
    }));
    return [ ...acc, ...dealsWithSameAgent ];
  }, []) : [];
  return [ ...(agent.bulletins || []), ...(agent.commercialBulletins || []), ...dealsEnriched ];
});

export const agentPageDealsSelector = createSelector([ safeAgentBulletinsByIdSelector, agentPageDataSelector ], (listings, agentData): UiAgentOfficeDeal[] => {
  if (!listings || !Array.isArray(listings.deals)) return [];

  return listings.deals.reduce((acc, d: IAgentOfficeDeal) => {
    if (!d.location) return acc;

    const isSeller = Array.isArray(d.sellers) && d.sellers.some((s: IAgentOfficeDealSeller) => s.agentId === agentData.agentId);
    const isBuyer = Array.isArray(d.buyers) && d.buyers.some((s: IAgentOfficeDealSeller) => s.agentId === agentData.agentId);

    const agentList = d.agents ? d.agents.filter(a => a.agentId === agentData.agentId) : [];
    if (!agentList.length) return acc;

    let side: DealAffiliationType;
    switch (true) {
      case isSeller && isBuyer:
        side = DealAffiliationType.Both;
        break;
      case isBuyer:
        side = DealAffiliationType.Buyer;
        break;
      case isSeller:
        side = DealAffiliationType.Seller;
        break;
      default:
    }

    const deal = {
      ...d,
      agents: [ agentList[0] ],
      side,
      key: uuid(),
    };

    return [ ...acc, deal ];
  }, []).sort((a, b) => +new Date(b.date) - +new Date(a.date));
});

export const agentDataForSoldDealBannerSelector = createSelector(safeAgentDataForSoldDealBannerSelector, (agent) => {
  if (!agent) return null;

  return {
    agentId: agent.agentId,
    name: agent.name,
    iconUrl: agent.iconUrl,
    virtualPhone: agent.virtualPhone,
    officeId: agent.officeId,
    phone1: agent.phone1,
  };
});
